/**
 * What the admin needs to know about this investor before planning for them.
 *
 * The user platform's planner sits on the investor's own dashboard, so their
 * portfolio is already in front of them. An admin arrives cold, so the same
 * facts are pulled in here: the MF investor profile and KYC readiness, the
 * holdings the portfolio report returns, and the lumpsum, SIP and servicing
 * orders already on the account.
 *
 * Every call is the admin panel's existing one — `fetchMFAdminDashboard` fans
 * out to the five report/order endpoints and reports per-section failures
 * rather than failing the page, which is what the warnings row renders.
 */

import React from "react";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { MFAdminDashboardData, MFRecord } from "../../../Api/mfAdminInvestments";
import { mfStatusLines, mfStatusTone } from "../../../Helpers/mfOrderStatus";
import styles from "./portfolio-planner.module.css";

export interface PlannerHolding {
  isin: string;
  name: string;
  category: string;
  invested: number;
  current: number;
  units: number;
}

const currency = (value: number, decimals = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: decimals,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export const normalizedStatus = (record: MFRecord) =>
  String(record.webhook_status || record.status || record.state || "Pending")
    .replace(/[_-]+/g, " ")
    .trim();

/* The planner's own tag palette for the shared tones. */
const TONE_TAG: Record<string, string> = {
  success: styles.tagGood,
  failed: styles.tagWarn,
  pending: styles.tagNeutral,
};

/**
 * Both statuses an order carries — its webhook status and its state — exactly
 * as the API returned them, each labelled (Helpers/mfOrderStatus, shared with
 * the MF Overview and the investor's MF dashboard). Display only: the planner's
 * SIP counts keep reading normalizedStatus.
 */
const StatusLines = ({ record }: { record: MFRecord }) => {
  const lines = mfStatusLines(record);
  if (!lines.length) return <>—</>;
  return (
    <div className={styles.statusStack}>
      {lines.map((line) => (
        <div key={line.label} className={styles.statusLine}>
          <span className={styles.statusLabel}>{line.label}</span>
          {line.value ? (
            <span className={`${TONE_TAG[mfStatusTone(line.value)]} ${styles.statusRaw}`}>
              {line.value}
            </span>
          ) : (
            <span className={styles.subtle}>—</span>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Aggregates the portfolio report's per-folio scheme rows into one row per
 * ISIN. Same source of truth as the investor dashboard's holdings tab — the
 * planner only needs the totals, not the folio-level redemption rules.
 */
export const summariseHoldings = (
  reports: MFRecord | null | undefined
): PlannerHolding[] => {
  const folios =
    reports &&
    reports.investor_reports &&
    reports.investor_reports.generate_holdings
      ? reports.investor_reports.generate_holdings.folios
      : null;
  if (!Array.isArray(folios)) return [];

  const details: Record<string, any> = {};
  (reports?.schemes_details || []).forEach((item: any) => {
    if (item && item.isin) details[item.isin] = item.data || {};
  });

  const aggregated: Record<string, PlannerHolding> = {};
  folios.forEach((folio: any) => {
    (folio?.schemes || []).forEach((scheme: any) => {
      if (!scheme || !scheme.isin) return;
      const meta = details[scheme.isin] || {};
      const existing =
        aggregated[scheme.isin] ||
        ({
          isin: scheme.isin,
          name: scheme.name || meta.longSchemeDescrip || scheme.isin,
          category:
            (meta.sclass_mst && (meta.sclass_mst.classname || meta.sclass_mst.category)) ||
            (meta.scheme_details && meta.scheme_details.AMFIType) ||
            "Mutual fund",
          invested: 0,
          current: 0,
          units: 0,
        } as PlannerHolding);
      existing.invested += Number(scheme.invested_value?.amount || 0);
      existing.current += Number(scheme.market_value?.amount || 0);
      existing.units += Number(scheme.holdings?.units || 0);
      aggregated[scheme.isin] = existing;
    });
  });

  return Object.keys(aggregated)
    .map((isin) => aggregated[isin])
    .sort((left, right) => right.current - left.current);
};

const MetricCard = ({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone?: "positive" | "negative";
}) => (
  <div className={styles.metricCard}>
    <div className={styles.metricTop}>
      <span>{label}</span>
      <span className={styles.metricIcon}>{icon}</span>
    </div>
    <div
      className={`${styles.metricValue} ${
        tone === "positive" ? styles.positive : tone === "negative" ? styles.negative : ""
      }`}
    >
      {value}
    </div>
    <div className={styles.metricHint}>{hint}</div>
  </div>
);

export const PlannerInvestorMetrics = ({
  holdings,
  orders,
  activeSips,
}: {
  holdings: PlannerHolding[];
  orders: MFRecord[];
  activeSips: number;
}) => {
  const invested = holdings.reduce((sum, holding) => sum + holding.invested, 0);
  const current = holdings.reduce((sum, holding) => sum + holding.current, 0);
  const gain = current - invested;
  return (
    <section className={styles.metricGrid}>
      <MetricCard
        label="Current value"
        value={currency(current)}
        hint={`${holdings.length} scheme${holdings.length === 1 ? "" : "s"} held`}
        icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
      />
      <MetricCard
        label="Invested value"
        value={currency(invested)}
        hint="Across available folios"
        icon={<ReceiptLongOutlinedIcon fontSize="small" />}
      />
      <MetricCard
        label="Total gain / loss"
        value={currency(gain)}
        hint={
          invested
            ? `${((gain / invested) * 100).toFixed(2)}% absolute`
            : "No holdings yet"
        }
        icon={<TrendingUpIcon fontSize="small" />}
        tone={gain >= 0 ? "positive" : "negative"}
      />
      <MetricCard
        label="Active SIPs"
        value={String(activeSips)}
        hint={`${orders.length} total order${orders.length === 1 ? "" : "s"}`}
        icon={<AutorenewIcon fontSize="small" />}
      />
    </section>
  );
};

const EmptyState = ({ text }: { text: string }) => (
  <div className={styles.empty}>
    <div className={styles.emptyIcon}>
      <AccountBalanceWalletOutlinedIcon />
    </div>
    {text}
  </div>
);

export const PlannerHoldingsTable = ({
  holdings,
}: {
  holdings: PlannerHolding[];
}) => {
  if (!holdings.length) {
    return (
      <EmptyState text="No mutual fund holdings were returned for this investor yet." />
    );
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Scheme</th>
            <th style={{ textAlign: "right" }}>Invested</th>
            <th style={{ textAlign: "right" }}>Current value</th>
            <th style={{ textAlign: "right" }}>Gain / loss</th>
            <th style={{ textAlign: "right" }}>Units</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((holding) => {
            const gain = holding.current - holding.invested;
            return (
              <tr key={holding.isin}>
                <td>
                  <span className={styles.strong}>{holding.name}</span>
                  <i className={styles.formula}>
                    {holding.category} · {holding.isin}
                  </i>
                </td>
                <td className={styles.numeric}>{currency(holding.invested)}</td>
                <td className={`${styles.numeric} ${styles.strong}`}>
                  {currency(holding.current)}
                </td>
                <td
                  className={`${styles.numeric} ${
                    gain >= 0 ? styles.positive : styles.negative
                  }`}
                >
                  {currency(gain)}
                </td>
                <td className={styles.numeric}>
                  {holding.units.toLocaleString("en-IN", {
                    maximumFractionDigits: 3,
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const PlannerOrdersTable = ({ orders }: { orders: MFRecord[] }) => {
  if (!orders.length) {
    return (
      <EmptyState text="No lumpsum or SIP orders were returned for this investor." />
    );
  }
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Scheme</th>
            <th>Type</th>
            <th style={{ textAlign: "right" }}>Amount</th>
            <th>Schedule</th>
            <th>Created</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => {
            const sip =
              order.order_type === "sip" ||
              order.systematic === true ||
              Boolean(order.purchase_plan_id);
            return (
              <tr key={`${order.purchase_plan_id || order.purchase_id || order.id || index}`}>
                <td>
                  <span className={styles.strong}>
                    {order.scheme_name || order.fund_name || order.scheme || "—"}
                  </span>
                  <i className={styles.formula}>
                    {order.purchase_plan_id || order.purchase_id || order.id || "—"}
                  </i>
                </td>
                <td>{sip ? "SIP" : "Lumpsum"}</td>
                <td className={styles.numeric}>
                  {currency(Number(order.amount || 0))}
                </td>
                <td className={styles.subtle}>
                  {sip
                    ? `${String(order.frequency || "—").replace(/[_-]+/g, " ")}${
                        order.installment_day ? ` · day ${order.installment_day}` : ""
                      }`
                    : "One-time"}
                </td>
                <td className={styles.subtle}>
                  {formatDate(order.created_at || order.inserted_at)}
                </td>
                <td>
                  <StatusLines record={order} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const PlannerWarnings = ({ data }: { data: MFAdminDashboardData | null }) =>
  data && data.warnings.length ? (
    <div className={styles.noticeWarn}>
      <WarningAmberIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
      <span>
        Some of this investor&apos;s data could not be loaded, so the context
        below is incomplete: {data.warnings.join(" · ")}
      </span>
    </div>
  ) : null;
